// src/components/principal/SubscriptionPanel.jsx
import React, { useEffect, useState } from "react";
import useSubscription, { formatLocalDate } from "../../hooks/useSubscription";
import useStripeActions from "../../hooks/useStripeActions";
import { STRIPE_PUBLISHABLE_KEY, DEFAULT_PRICE_ID } from "../../config/stripe";

const SubscriptionPanel = () => {
  const { status, meta, refresh } = useSubscription({ enabled: true });

  const { startSubscription, openCustomerPortal, loading } = useStripeActions({
    publishableKey: STRIPE_PUBLISHABLE_KEY,
    priceId: DEFAULT_PRICE_ID,
  });

  // Only show the “Stripe is loading…” overlay if it takes longer than 400ms
  const [showStripeOverlay, setShowStripeOverlay] = useState(false);
  const [localError, setLocalError] = useState("");

  useEffect(() => {
    if (!loading) {
      setShowStripeOverlay(false);
      return;
    }
    const id = setTimeout(() => setShowStripeOverlay(true), 400);
    return () => clearTimeout(id);
  }, [loading]);

  const canUsePortal = Boolean(meta?.stripe_subscription_id);

  const handleStartSubscription = async () => {
    setLocalError("");
    try {
      await startSubscription(); // calls checkout session
      await refresh();
    } catch (err) {
      console.error(err);
      setLocalError(
        err?.response?.data?.detail ||
          err?.message ||
          "Unable to start subscription. Please try again."
      );
    }
  };

  const handleOpenPortal = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setLocalError("");
    try {
      await openCustomerPortal(); // backend returns portal URL; hook should redirect
    } catch (err) {
      console.error(err);
      setLocalError(
        err?.response?.data?.detail ||
          err?.message ||
          "Unable to open Customer Portal. Please try again."
      );
    }
  };

  const PortalLinkButton = ({ children }) => (
    <button
      type="button"
      onClick={handleOpenPortal}
      className="template-download-link"
      style={{
        textDecoration: "underline",
        background: "none",
        border: "none",
        cursor: "pointer",
        padding: 0,
        color: "inherit",
      }}
    >
      {children}
    </button>
  );

  // === TRIALING ==========================================================
  if (status === "TRIALING") {
    return (
      <div className="subscription-content">
        <h3>Manage Subscription:</h3>
        {canUsePortal ? (
          <p>
            Your 7-day free trial is active. You can complete your payment in the{" "}
            <PortalLinkButton>Customer Portal</PortalLinkButton> any time before the trial ends
            to keep your account and its data.
          </p>
        ) : (
          <>
            <p className="no-subscription-message">
              Your School is in a 7-day free trial period. To keep your accounts and data after the
              trial ends, you can start your Subscription now. Alternatively, you can continue and
              Subscribe later.
            </p>
            <button
              onClick={handleStartSubscription}
              disabled={loading}
              className="start-subscription-button"
            >
              Exit Trial Period and Subscribe to GhrianGhraffy
            </button>
          </>
        )}

        {localError && <p className="error-message">{localError}</p>}

        {showStripeOverlay && (
          <div className="stripe-overlay">
            <div className="stripe-overlay-backdrop" />
            <div className="stripe-overlay-content">
              <p>Connecting securely to Stripe…</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // === ACTIVE (not canceling) ============================================
  if (status === "ACTIVE" && !meta?.subscription_cancel_at_period_end) {
    return (
      <div className="subscription-content">
        <h3>Manage Subscription:</h3>
        {canUsePortal ? (
          <p>
            Your subscription is Active. Manage it in the{" "}
            <PortalLinkButton>Customer Portal</PortalLinkButton>.
          </p>
        ) : (
          <>
            <p>Your subscription is Active.</p>
            <button
              onClick={handleStartSubscription}
              disabled={loading}
              className="start-subscription-button"
            >
              Update Billing
            </button>
          </>
        )}

        {localError && <p className="error-message">{localError}</p>}

        {showStripeOverlay && (
          <div className="stripe-overlay">
            <div className="stripe-overlay-backdrop" />
            <div className="stripe-overlay-content">
              <p>Connecting securely to Stripe…</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // === ACTIVE (canceling at period end) =================================
  if (status === "ACTIVE" && meta?.subscription_cancel_at_period_end) {
    const endISO =
      meta.subscription_current_period_end_iso || meta.subscription_cancel_at_iso || null;

    return (
      <div className="subscription-content">
        <h3>Manage Subscription:</h3>
        <p>
          <strong>Canceling:</strong> Your subscription will end on{" "}
          <strong>{formatLocalDate(endISO)}</strong>. You can change this in the{" "}
          {canUsePortal ? <PortalLinkButton>Customer Portal</PortalLinkButton> : "Customer Portal"}.
        </p>

        {localError && <p className="error-message">{localError}</p>}

        {showStripeOverlay && (
          <div className="stripe-overlay">
            <div className="stripe-overlay-backdrop" />
            <div className="stripe-overlay-content">
              <p>Connecting securely to Stripe…</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // === INACTIVE or anything else ========================================
  return (
    <div className="subscription-content">
      <h3>Manage Subscription:</h3>
      <p className="no-subscription-message">
        You don’t have an active subscription. Start a subscription to enable staff uploads and
        keep your school’s accounts and data.
      </p>
      <button
        onClick={handleStartSubscription}
        disabled={loading}
        className="start-subscription-button"
      >
        Subscribe to GhrianGhraffy
      </button>

      {localError && <p className="error-message">{localError}</p>}

      {showStripeOverlay && (
        <div className="stripe-overlay">
          <div className="stripe-overlay-backdrop" />
          <div className="stripe-overlay-content">
            <p>Connecting securely to Stripe…</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPanel;
